import { User, Building2, ArrowRight, MousePointerClick, Rocket, Briefcase, Stethoscope, HeartPulse } from "lucide-react";

interface PathCardProps {
  variant: "individual" | "treatment";
}

const cardData = {
  individual: {
    icon: User,
    title: "I Am an Individual\nin Recovery",
    description:
      "Personal coaching for job readiness, financial skills, and building your next chapter.",
    buttonText: "Get Started",
    href: "/clients",
  },
  treatment: {
    icon: Building2,
    title: "I Am a Treatment\nCenter",
    description:
      "Partner with us to provide job readiness and financial education for your clients.",
    buttonText: "Learn More",
    href: "/centers",
  },
};

import React, { memo, useCallback, useId, forwardRef } from 'react';
import { cn, EmptyState } from "../../components/ui/interactive-empty-state";

export function PathCard({ variant }: PathCardProps) {
  const { icon: Icon, title, description, buttonText, href } =
    cardData[variant];
  const isIndividual = variant === "individual";

  const icons = isIndividual
    ? [<Briefcase key="p1" className="h-6 w-6" />, <User key="p2" className="size-8" />, <Rocket key="p3" className="h-6 w-6" />]
    : [<Stethoscope key="p1" className="h-6 w-6" />, <Building2 key="p2" className="size-8" />, <HeartPulse key="p3" className="h-6 w-6" />]
  return (
    <EmptyState
      className={cn(
        "relative w-[340px] h-[380px] rounded-3xl overflow-hidden block transition-transform hover:scale-[1.02] outline-none",
        isIndividual
          ? "bg-gradient-to-b from-[#f7941d] to-[#e8850a] shadow-[0_16px_48px_rgba(247,148,29,0.3)] border-0"
          : "bg-white shadow-[0_8px_32px_rgba(0,0,0,0.08)] border border-[#e2e8f0]"
      )}
      title={title}
      size="lg"
      description={description}
      icons={icons}
      action={{ label: buttonText, icon: <MousePointerClick className="h-4 w-4" />, onClick: () => window.location.href = href }}
    />

  )

  return (
    <a
      href={href}
      className="relative w-[340px] h-[380px] rounded-3xl overflow-hidden block transition-transform hover:scale-[1.02]"
      style={{
        background: isIndividual
          ? "linear-gradient(180deg, #f7941d 0%, #e8850a 100%)"
          : "#ffffff",
        boxShadow: isIndividual
          ? "0 16px 48px rgba(247,148,29,0.3)"
          : "0 8px 32px rgba(0,0,0,0.08)",
        border: isIndividual ? "0" : "1px solid #e2e8f0",
        outline: "none",
        outlineOffset: 0,
      }}
    >
      {/* Pattern Strip */}
      <PatternStrip isIndividual={isIndividual} />

      {/* Icon Container */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-[56px] w-[80px] h-[80px] rounded-2xl flex items-center justify-center"
        style={{
          backgroundColor: isIndividual
            ? "rgba(255,255,255,0.2)"
            : "rgba(26,39,68,0.1)",
        }}
      >
        <Icon className="w-10 h-10 text-[#1a2744]" />
      </div>

      {/* Title */}
      <div
        className="absolute top-[152px] left-0 w-full text-center text-[20px] font-bold leading-[1.3] whitespace-pre-line"
        style={{ color: "#1a2744" }}
      >
        {title}
      </div>

      {/* Description */}
      <p
        className="absolute top-[216px] left-[32px] w-[276px] text-center text-[13px] leading-[1.5]"
        style={{
          color: isIndividual ? "rgba(26,39,68,0.6)" : "rgba(100,112,130,0.6)",
        }}
      >
        {description}
      </p>

      {/* Button */}
      <div className="absolute bottom-[32px] left-[32px] w-[276px] h-12 bg-[#1a2744] rounded-xl flex items-center justify-center gap-2">
        <span className="text-[15px] font-semibold text-white">{buttonText}</span>
        <ArrowRight className="w-4 h-4 text-white" />
      </div>
    </a>
  );
}

function PatternStrip({ isIndividual }: { isIndividual: boolean }) {
  const dotColor = isIndividual
    ? "rgba(255,255,255,0.18)"
    : "rgba(26,39,68,0.12)";

  // Generate dots - 2x more dense (12 rows x 68 columns, 2px dots, 5px spacing)
  const dots = [];
  for (let row = 0; row < 12; row++) {
    for (let col = 0; col < 68; col++) {
      dots.push(
        <div
          key={`${row}-${col}`}
          className="absolute w-[2px] h-[2px] rounded-full"
          style={{
            backgroundColor: dotColor,
            left: 3 + col * 5,
            top: 3 + row * 5,
          }}
        />
      );
    }
  }

  return (
    <div className="absolute top-0 left-0 w-full h-10 overflow-hidden">
      {dots}
    </div>
  );
}















